/** MELANUA cart drawer presentation. Cart state and actions remain in CartContext. */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePreview } from '../context/PreviewContext';
import './CartDrawer.css';

function CartItem({ item, onUpdateQuantity, onRemove, onNavigate }) {
    const { product, variant, quantity, unit_price, line_total } = item;
    return (
        <article className="me-bag-item">
            <Link to={`/product/${product.slug}/`} onClick={onNavigate} className="me-bag-item-image">
                <img src={product.image_url || '/images/placeholder.jpg'} alt={product.name} />
            </Link>
            <div className="me-bag-item-info">
                <Link to={`/product/${product.slug}/`} onClick={onNavigate} className="me-bag-item-name">{product.name}</Link>
                {variant && <p className="me-bag-item-variant">{variant.name}</p>}
                <p className="me-bag-unit-price">A${parseFloat(unit_price).toFixed(2)} each</p>
                <div className="me-bag-item-actions">
                    <div className="me-bag-quantity" aria-label={`Quantity for ${product.name}`}>
                        <button type="button" onClick={() => onUpdateQuantity(item.id, Math.max(1, quantity - 1))} disabled={quantity <= 1} aria-label={`Decrease quantity of ${product.name}`}>−</button>
                        <span aria-live="polite">{quantity}</span>
                        <button type="button" onClick={() => onUpdateQuantity(item.id, quantity + 1)} aria-label={`Increase quantity of ${product.name}`}>+</button>
                    </div>
                    <button type="button" className="me-bag-remove" onClick={() => onRemove(item.id)} aria-label={`Remove ${product.name}`}>Remove</button>
                </div>
            </div>
            <p className="me-bag-line-total">A${parseFloat(line_total).toFixed(2)}</p>
        </article>
    );
}

export default function CartDrawer() {
    const { cart, isCartOpen, closeCart, updateItem, removeItem, isLoading } = useCart();
    const { isPreviewMode } = usePreview();
    const drawerRef = useRef(null);
    const closeRef = useRef(null);

    useEffect(() => {
        if (!isCartOpen || !isPreviewMode) return undefined;
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closeCart();
            if (event.key !== 'Tab' || !drawerRef.current) return;
            const focusable = [...drawerRef.current.querySelectorAll('a[href], button:not([disabled])')];
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus?.();
        };
    }, [isCartOpen, isPreviewMode, closeCart]);

    if (!isCartOpen || !isPreviewMode) return null;

    const handleUpdateQuantity = async (itemId, quantity) => { await updateItem(itemId, quantity); };
    const handleRemove = async (itemId) => { await removeItem(itemId); };
    const hasItems = Boolean(cart.items?.length);

    return (
        <div className="me-bag-layer">
            <div className="me-bag-backdrop" onClick={closeCart} aria-hidden="true" />
            <aside className="me-bag-drawer" ref={drawerRef} role="dialog" aria-modal="true" aria-labelledby="me-bag-title">
                <header className="me-bag-header">
                    <div>
                        <p className="me-bag-eyebrow">THE HOUSE / MELANUA®</p>
                        <h2 id="me-bag-title">Your Bag</h2>
                    </div>
                    <button ref={closeRef} type="button" className="me-bag-close" onClick={closeCart} aria-label="Close bag">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
                    </button>
                </header>
                <div className="me-bag-content">
                    {isLoading ? (
                        <div className="me-bag-loading" role="status">Updating your bag…</div>
                    ) : !hasItems ? (
                        <div className="me-bag-empty">
                            <p className="me-bag-empty-index">01 / THE CREATIONS</p>
                            <h3>Your bag is empty.</h3>
                            <p>The House is waiting.</p>
                            <Link to="/products/" onClick={closeCart} className="me-bag-primary">EXPLORE THE CREATIONS <span aria-hidden="true">↗</span></Link>
                        </div>
                    ) : (
                        <div className="me-bag-items">
                            <p className="me-bag-count">{cart.item_count} {cart.item_count === 1 ? 'item' : 'items'} in your bag</p>
                            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={handleUpdateQuantity} onRemove={handleRemove} onNavigate={closeCart} />)}
                        </div>
                    )}
                </div>
                {hasItems && (
                    <div className="me-bag-summary">
                        <div className="me-bag-subtotal"><span>Subtotal</span><strong>A${parseFloat(cart.subtotal).toFixed(2)} AUD</strong></div>
                        <p>Delivery and taxes are calculated at checkout.</p>
                        <Link to="/checkout/" onClick={closeCart} className="me-bag-primary">PROCEED TO CHECKOUT <span aria-hidden="true">↗</span></Link>
                        <button type="button" onClick={closeCart} className="me-bag-continue">Continue browsing</button>
                    </div>
                )}
            </aside>
        </div>
    );
}
